import React, { useEffect, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import { useNavigate } from '@reach/router';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';

import useSearch from 'hooks/useSearch';
import SearchItem from 'components/SearchItem/SearchItem';
import withUserContext from 'behaviors/withUserContext';
import mixpanel from 'mixpanel-browser';
import CauzeButton from 'components/CauzeButton/CauzeButton';
import TopicList from 'components/TopicList/TopicList';
import useDashboardStore from 'stores/DashboardStore';
import useSearchStore from 'stores/SearchStoreV2';
import useThemeStore from 'stores/ThemeStore';

const PAGE_SIZE = 12;
const INTERSTITIAL_INDEX = 3;

const SearchView = ({ activeEntity, uiStore, authStore, userProfileStore }) => {
  const searchStore = useSearchStore();
  const navigate = useNavigate();
  const { setProps } = useDashboardStore();
  const themeStore = useThemeStore();

  useSearch({
    matchId:
      themeStore.theme === themeStore.THEMES.ALTMAN_SOLON
        ? userProfileStore.companyMatch?.id
        : null,
    forceMatch: themeStore.theme === themeStore.THEMES.ALTMAN_SOLON,
  });

  const showResults = searchStore.results.length > 0 && !searchStore.loading;
  const showEmpty = searchStore.results.length === 0 && !searchStore.loading;
  const canLoadMore = searchStore.page * PAGE_SIZE < searchStore.results.length;

  const onDonate = (charityId) => {
    mixpanel.track('Search Result Click', {
      channel: 'web',
      type: 'charity',
      id: charityId,
      withGift: !!uiStore.savedUiState.userToken,
    });

    navigate(`/charity/${charityId}`);
  };

  const showUnauthedPrompt = () => {
    uiStore.openModal('CONFIRM', {
      message: 'Please Login or Signup to follow givers',
      action: () => {
        navigate('/login');
      },
      confirmText: 'Login / Signup',
      cancelText: 'Not Now',
    });
  };

  const topics = useMemo(() => {
    const _topics = searchStore.topics.map((t) => t.name);
    const _categories = searchStore.categories;

    return [..._topics, ..._categories];
  }, [searchStore.topics, searchStore.categories]);

  useEffect(() => {
    searchStore.getCharities();
  }, []);

  useEffect(() => {
    setProps({
      title: 'Your Search Results',
      size: 'half',
    });

    return () => {
      searchStore.updateSearch({
        query: '',
      });
    };
  }, []);

  return (
    <div className="search-view flex-column">
      <div>
        <div>
          <div className="results-container gap-y-3.5 flex flex-col">
            <div>
              <TopicList
                title="Trending Topics"
                topics={topics}
                activeTopics={searchStore.currentSearch.topics}
                loading={searchStore.loading}
              />
            </div>
            {searchStore.loading && <CauzeSpinner fullscreen />}
            {showEmpty && (
              <div className="empty-item flex-column flex-align-center">
                <div className="flex-row">
                  <div className="flex-column">
                    <p className="text-bold">Sorry, We Couldn't Find a Match</p>
                    <p className="text-normal">
                      Try a different search item or check out
                      <br />
                      some of our suggestions below
                    </p>
                  </div>
                </div>
                <div className="flex-row flex-justify-end flex-align-center"></div>
              </div>
            )}
            {showResults &&
              searchStore.results.map((charity, index) => (
                <SearchItem
                  charity={charity}
                  userContext={activeEntity.userContext}
                  onLearnMore={() => {
                    uiStore.openModal('PROFILE_MODAL', {
                      id: charity.id,
                      entityType: 'CHARITY',
                    });
                  }}
                  onFollowCharity={searchStore.followCharity}
                  onFollowGiver={searchStore.onFollowGiver}
                  key={`${index}-${charity.name}`}
                  index={index}
                  isAuthenticated={authStore.isAuthenticated}
                  onShowUnauthedPrompt={showUnauthedPrompt}
                  authStore={authStore}
                />
              ))}
          </div>
        </div>
        <div>
          <div className="results-container">
            {searchStore.loadingMore && <CauzeSpinner />}
            {canLoadMore && !searchStore.loading && !searchStore.loadingMore && (
              <div className="w-full flex">
                <div className="mx-auto">
                  <button
                    className="cz-btn-outline brand mt-5"
                    onClick={() => {
                      searchStore.getCharities(true);
                    }}
                  >
                    Load More Results
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withUserContext(
  inject('uiStore', 'authStore', 'userProfileStore')(observer(SearchView)),
);
